/**
 * @fileoverview react-i18next configuration.
 * @module i18n
 * @description
 * Mirrors the Angular ngx-translate setup from `app.config.ts`:
 *   - http backend loading `/assets/i18n/{{lng}}.json`
 *   - `fallbackLng: 'en'`
 *   - default language from the persisted language-store value (`userLanguage`
 *     localStorage key), falling back to 'en'.
 *
 * Key structure: `en.json` uses nested objects (e.g. `{ "login": { "title": ... } }`),
 * so the react-i18next defaults — `keySeparator: '.'` and `nsSeparator: ':'` — resolve
 * dot-notation keys like `'login.title'` exactly as ngx-translate did. No overrides needed.
 *
 * @author Liraz Amir
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';

/** localStorage key for the saved language preference (matches the language store / Angular). */
const LANGUAGE_STORAGE_KEY = 'userLanguage';

/** Read the persisted language preference, defaulting to 'en'. */
function getInitialLanguage(): string {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'en';
  } catch {
    return 'en';
  }
}

void i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    backend: {
      loadPath: '/assets/i18n/{{lng}}.json',
    },
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    supportedLngs: ['en'],
    interpolation: {
      // React already escapes values, so disable i18next escaping.
      escapeValue: false,
    },
  });

export default i18n;
export { i18n };
